import React from 'react';
import { User as UserIcon, MapPin, ShieldCheck, QrCode, Camera, Crown } from 'lucide-react';
import type { User } from '@/shared/types';
import type { Unit } from '@yawi/types';
import { getInitials } from '@yawi/infrastructure';
import { useSelectedUnit } from '@/app/providers';

interface ProfileHeaderProps {
  user: User;
  onAvatarUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Misma lógica que UserMenuToggleV2 — la unidad activa (units[]) es la
// fuente de verdad, no el residenceInfo legacy que ya no se mantiene.
const getUnitLabel = (unit: Unit): string => {
  if (unit.name) return unit.name;
  if (unit.tower) return `Torre ${unit.tower} - ${unit.number || unit.floor || ''}`.trim();
  return `${unit.street || ''} #${unit.exteriorNumber || unit.number || ''}`.trim() || 'Unidad';
};

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ user, onAvatarUpload }) => {
  const { selectedUnit, selectedUnitId } = useSelectedUnit();

  // Con varias unidades, "ser principal" depende de la unidad activa; con una
  // sola, el campo plano isPrincipal del usuario ya refleja lo mismo.
  const isPrincipalOfSelectedUnit = selectedUnitId
    ? (user.units?.find((assignment) => assignment.unitId === selectedUnitId)?.isPrincipal ?? false)
    : (user.isPrincipal ?? false);

  return (
    <div className="bg-surface rounded-2xl shadow-sm border border-theme p-6 md:p-8 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 dark:bg-primary-900/20 rounded-full -mr-20 -mt-20 blur-3xl opacity-50"></div>

      <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
        {/* Avatar with visible ring */}
        <div className="relative shrink-0">
          <div className="absolute inset-0 rounded-full">
            <div className="w-full h-full rounded-full"></div>
          </div>
          <div className="relative w-20 h-20 rounded-full overflow-hidden shadow-md group cursor-pointer mt-[2px] ml-[2px]">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 text-white text-xl font-bold">
                {getInitials(user.name)}
              </div>
            )}
            <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer rounded-full">
              <Camera className="w-5 h-5 text-white" />
              <input type="file" className="hidden" accept="image/*" onChange={onAvatarUpload} />
            </label>
          </div>
        </div>

        {/* Identity Block */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex flex-col md:gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 text-theme tracking-tight">{user.name}</h2>
            <p className="text-gray-500 dark:text-gray-400 flex items-center justify-center md:justify-start gap-1.5 text-sm">
              <MapPin size={14} />
              <span>{selectedUnit ? getUnitLabel(selectedUnit) : 'Sin unidad asignada'}</span>
            </p>
          </div>
          
          {/* Colorful Badge System */}
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-4">
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 shadow-sm">
              <UserIcon size={12} />
              <span>{user.role}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span>{user.status}</span>
            </span>
            {isPrincipalOfSelectedUnit && (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 shadow-sm">
                <Crown size={12} />
                <span>Principal</span>
              </span>
            )}
          </div>
        </div>

        {/* QR Code */}
        <div className="shrink-0 bg-white bg-surface-elevated p-3 rounded-xl shadow-sm border border-gray-100">
          <QrCode size={56} className="text-gray-700 text-theme" />
        </div>
      </div>
    </div>
  );
};
